/**
 * Stable badge bits shared by profile presentation code.
 *
 * Keep these values in sync with `UserBadges` in stoat.js and the API model.
 */
export const PROFILE_BADGE_BITS = {
  developer: 1,
  translator: 2,
  supporter: 4,
  responsibleDisclosure: 8,
  founder: 16,
  platformModeration: 32,
  activeSupporter: 64,
  paw: 128,
  earlyAdopter: 256,
  reservedRelevantJokeBadge1: 512,
  reservedRelevantJokeBadge2: 1024,
  verified: 2048,
} as const;

export type ProfileBadgeKey = keyof typeof PROFILE_BADGE_BITS;

export type ProfileBadgeAppearance = {
  icon: string;
  colour: string;
  description: string;
};

const PROFILE_BADGE_APPEARANCE: Record<
  ProfileBadgeKey,
  ProfileBadgeAppearance
> = {
  verified: {
    icon: "✓",
    colour: "#5865F2",
    description: "Identity verified by IDF Messenger",
  },
  founder: {
    icon: "✦",
    colour: "#FBBF24",
    description: "Founder of IDF Messenger",
  },
  platformModeration: {
    icon: "◇",
    colour: "#60A5FA",
    description: "Member of the platform moderation team",
  },
  developer: {
    icon: "</>",
    colour: "#5B8CFF",
    description: "Contributed to IDF Messenger development",
  },
  responsibleDisclosure: {
    icon: "◆",
    colour: "#34D399",
    description: "Responsibly disclosed a security issue",
  },
  activeSupporter: {
    icon: "♥",
    colour: "#FB7185",
    description: "Actively supports the project",
  },
  supporter: {
    icon: "♥",
    colour: "#F472B6",
    description: "Supported the development of IDF Messenger",
  },
  translator: {
    icon: "文",
    colour: "#A78BFA",
    description: "Helped translate IDF Messenger",
  },
  earlyAdopter: {
    icon: "◈",
    colour: "#2DD4BF",
    description: "One of the first members of the community",
  },
  paw: {
    icon: "🐾",
    colour: "#F59E0B",
    description: "A special community badge",
  },
  reservedRelevantJokeBadge1: {
    icon: "ඞ",
    colour: "#EF4444",
    description: "A collectible secret badge",
  },
  reservedRelevantJokeBadge2: {
    icon: "☻",
    colour: "#94A3B8",
    description: "A collectible secret badge",
  },
};

const PROFILE_BADGE_ORDER: readonly ProfileBadgeKey[] = [
  "verified",
  "founder",
  "platformModeration",
  "developer",
  "responsibleDisclosure",
  "activeSupporter",
  "supporter",
  "translator",
  "earlyAdopter",
  "paw",
  "reservedRelevantJokeBadge1",
  "reservedRelevantJokeBadge2",
];

export function getVisibleProfileBadgeKeys(
  badges: number,
  isPlatformStaff = false,
) {
  const effectiveBadges = isPlatformStaff
    ? badges | PROFILE_BADGE_BITS.verified
    : badges;

  return PROFILE_BADGE_ORDER.filter(
    (key) => (effectiveBadges & PROFILE_BADGE_BITS[key]) !== 0,
  );
}

export function isVerifiedProfile(badges: number, isPlatformStaff = false) {
  return isPlatformStaff || (badges & PROFILE_BADGE_BITS.verified) !== 0;
}

export function getProfileBadgeAppearance(
  badge: ProfileBadgeKey,
): ProfileBadgeAppearance {
  return PROFILE_BADGE_APPEARANCE[badge];
}
